import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../core/data/routes';
import { useSessionsStore } from '../store/sessionsStore';
import { usePaymentStore } from '../../payment/store/paymentStore';
import {
	AppBar,
	LoadingWidget,
	FadeInAnimation,
} from '../../../core/components/ui';
import { CurrencyFormatter } from '../../../core/utils/formatters';
import toast from 'react-hot-toast';

const PACKAGES = [
	{ credits: 1, label: 'Single', popular: false },
	{ credits: 3, label: '3 Pack', popular: true },
	{ credits: 5, label: '5 Pack', popular: false },
	{ credits: 10, label: '10 Pack', popular: false },
];

const BuySessionPage: React.FC = () => {
	const navigate = useNavigate();
	const sessionsStore = useSessionsStore();
	const paymentStore = usePaymentStore();
	const user = sessionsStore.retrieve();

	const [feePerSession, setFeePerSession] = useState(100);
	const [currency, setCurrency] = useState('GHS');
	const [selectedPkg, setSelectedPkg] = useState(1);
	const [currentCredits, setCurrentCredits] = useState(0);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		setLoading(true);

		try {
			const credit = await sessionsStore.getSessionCredit();

			setFeePerSession(100);
			setCurrency('GHS');
			setCurrentCredits(credit.quantity || 0);
		} catch (error) {
		} finally {
			setLoading(false);
		}
	};

	const selected =
		PACKAGES.find((p) => p.credits === selectedPkg) || PACKAGES[0];
	const totalCost = feePerSession * selected.credits;

	const handleBuy = async () => {
	if (!user) {
		toast.error('User not found. Please log in again.');
		return;
	}

	// Guard: make sure we actually have what the backend needs
	if (!user.email || !user.phoneNumber || !user.firstName || !user.lastName) {
		toast.error('Your profile is missing required info (name, email, or phone). Please update your profile.');
		return;
	}

	setLoading(true);
	try {
		// Initiate payment
		const paymentResult = await paymentStore.initiatePayment({
			customerId: user.id,
			amount: totalCost,
			currency,
			credits: selected.credits,
			email: user.email,
			phone: user.phoneNumber,
			name: `${user.firstName} ${user.lastName}`,
		});

		if (paymentResult?.checkoutURL) {
			if (paymentResult.transactionId) {
				sessionStorage.setItem('therapp_pending_payment_id', paymentResult.transactionId);
			}
			// window.open(paymentResult.checkoutURL);
			window.location.assign(paymentResult.checkoutURL);
		} else {
			toast.error('Could not initiate payment. Please try again.');
		}
	} catch {
		toast.error('Payment initiation failed.');
	} finally {
		setLoading(false);
	}
};

	return (
		<div className='page'>
			{loading && <LoadingWidget />}
			<AppBar
				title='Buy Sessions credits'
				onBack={() => navigate(-1)}
			/>

			<div
				style={{
					flex: 1,
					overflowY: 'auto',
					padding: '20px 20px 40px',
					display: 'flex',
					flexDirection: 'column',
					gap: 20,
				}}>
				{/* Current credits */}
				<FadeInAnimation delay={0}>
					<div
						style={{
							padding: '20px',
							background: 'var(--color-primary)',
							borderRadius: 20,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							color: 'white',
						}}>
						<div>
							<div
								style={{
									fontSize: 12,
									opacity: 0.7,
									textTransform: 'uppercase',
									letterSpacing: 0.8,
								}}>
								Current Credits
							</div>
							<div style={{ fontSize: 36, fontWeight: 800 }}>
								{currentCredits}
							</div>
						</div>
						<svg
							width='40'
							height='40'
							viewBox='0 0 24 24'
							fill='rgba(255,255,255,0.3)'>
							<path d='M21 18v1a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v1h-9a2 2 0 00-2 2v8a2 2 0 002 2h9z' />
							<path d='M23 13v-2a1 1 0 00-1-1h-8a1 1 0 00-1 1v4a1 1 0 001 1h8a1 1 0 001-1v-2z' />
						</svg>
					</div>
				</FadeInAnimation>

				{/* Package selection */}
				<FadeInAnimation delay={0.1}>
					<div>
						<div
							style={{
								fontSize: 14,
								fontWeight: 700,
								marginBottom: 12,
								color: 'var(--color-on-surface)',
							}}>
							Select Package
						</div>
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr',
								gap: 10,
							}}>
							{PACKAGES.map((pkg) => (
								<button
									key={pkg.credits}
									onClick={() => setSelectedPkg(pkg.credits)}
									style={{
										padding: '16px 12px',
										borderRadius: 14,
										border:
											selectedPkg === pkg.credits
												? '2px solid var(--color-primary)'
												: '2px solid var(--color-outline-variant)',
										background:
											selectedPkg === pkg.credits
												? 'var(--color-primary-container)'
												: 'var(--color-surface)',
										position: 'relative',
										transition: 'all 0.2s',
										cursor: 'pointer',
									}}>
									{pkg.popular && (
										<span
											style={{
												position: 'absolute',
												top: -10,
												left: '50%',
												transform: 'translateX(-50%)',
												padding: '2px 10px',
												borderRadius: 99,
												background: 'var(--color-accent)',
												color: 'white',
												fontSize: 10,
												fontWeight: 700,
												whiteSpace: 'nowrap',
											}}>
											POPULAR
										</span>
									)}
									<div
										style={{
											fontSize: 26,
											fontWeight: 800,
											color:
												selectedPkg === pkg.credits
													? 'var(--color-primary)'
													: 'var(--color-on-surface)',
										}}>
										{pkg.credits}
									</div>
									<div
										style={{
											fontSize: 12,
											fontWeight: 600,
											color: 'var(--color-on-surface-variant)',
										}}>
										{pkg.label}
									</div>
									<div
										style={{
											fontSize: 13,
											fontWeight: 700,
											marginTop: 4,
											color:
												selectedPkg === pkg.credits
													? 'var(--color-primary)'
													: 'var(--color-on-surface)',
										}}>
										{currency} {(feePerSession * pkg.credits).toLocaleString()}
									</div>
								</button>
							))}
						</div>
					</div>
				</FadeInAnimation>

				{/* Summary */}
				<FadeInAnimation delay={0.2}>
					<div className='card'>
						<div
							style={{
								fontWeight: 700,
								fontSize: 14,
								marginBottom: 12,
								color: 'var(--color-primary)',
							}}>
							Order Summary
						</div>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								marginBottom: 8,
							}}>
							<span
								style={{
									fontSize: 13,
									color: 'var(--color-on-surface-variant)',
								}}>
								{selected.credits} session credit
								{selected.credits > 1 ? 's' : ''}
							</span>
							<span style={{ fontSize: 13, fontWeight: 600 }}>
								{currency} {totalCost.toLocaleString()}
							</span>
						</div>
						<div
							style={{
								height: 1,
								background: 'var(--color-outline-variant)',
								margin: '10px 0',
							}}
						/>
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<span style={{ fontSize: 14, fontWeight: 700 }}>Total</span>
							<span
								style={{
									fontSize: 16,
									fontWeight: 800,
									color: 'var(--color-primary)',
								}}>
								{currency} {totalCost.toLocaleString()}
							</span>
						</div>
					</div>
				</FadeInAnimation>

				<FadeInAnimation delay={0.3}>
					<button
						className='btn-gradient'
						onClick={handleBuy}
						disabled={loading}>
						Proceed to Payment
					</button>
				</FadeInAnimation>

				<FadeInAnimation delay={0.4}>
					<p
						style={{
							fontSize: 12,
							color: 'var(--color-on-surface-variant)',
							textAlign: 'center',
							lineHeight: 1.5,
						}}>
						Payments are processed securely. Credits are non-refundable.
					</p>
				</FadeInAnimation>
			</div>
		</div>
	);
};

export default BuySessionPage;
